import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Snackbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { COLORS } from '../../theme';

const TIPOS = ['success', 'error', 'warning', 'info'];
const DURACAO_PADRAO = 4000;
const TICK_MS = 100;
const RAIO = '16px';

/** Tom e ícone de cada severidade (cores sempre vindas do `theme.js`). */
const TONS = {
  success: { cor: COLORS.successDark, Icone: CheckCircleRoundedIcon },
  error: { cor: COLORS.errorDark, Icone: ErrorRoundedIcon },
  warning: { cor: COLORS.warningDark, Icone: WarningAmberRoundedIcon },
  info: { cor: COLORS.infoDark, Icone: InfoRoundedIcon },
};

const POSICAO_PADRAO = { vertical: 'top', horizontal: 'center' };
// No celular o alerta nasce no rodapé: fica perto do polegar e não colide com o
// header fixo (que ocupa os primeiros ~112px da tela).
const POSICAO_MOBILE = { vertical: 'bottom', horizontal: 'center' };

/**
 * Alerta único do sistema (Snackbar + superfície própria + progresso).
 *
 * Layout: chip de ícone à esquerda, título/mensagem empilhados, ação opcional e
 * fechar em botão fantasma. O progresso fica aninhado na base do card com
 * `overflow: hidden`, portanto é recortado pelo raio (não vaza pelos cantos).
 *
 * Controlado por props para funcionar tanto no `AlertaHost` global quanto em
 * qualquer tela que prefira gerenciar o próprio estado.
 */
const AlertaReutilizavel = ({
  id = null,
  mensagem,
  tipo = 'info',
  aberto = false,
  onFechar,
  titulo = null,
  autoHideDuration = DURACAO_PADRAO,
  posicao = null,
  variant = 'standard',
  acao = null,
  pausarNoHover = true,
}) => {
  const theme = useTheme();
  const escuro = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const origem = posicao || (isMobile ? POSICAO_MOBILE : POSICAO_PADRAO);
  const [progresso, setProgresso] = useState(100);
  const pausadoRef = useRef(false);

  const duracao = useMemo(() => {
    const valor = Number(autoHideDuration);
    return Number.isFinite(valor) && valor > 0 ? valor : DURACAO_PADRAO;
  }, [autoHideDuration]);

  const severidade = TIPOS.includes(tipo) ? tipo : 'info';

  useEffect(() => {
    if (!aberto) {
      pausadoRef.current = false;
      setProgresso(100);
      return undefined;
    }

    // Um alerta novo nunca herda a pausa do anterior: senão a fila ficaria
    // travada aberta depois que o usuário passasse o mouse por cima do card.
    pausadoRef.current = false;
    setProgresso(100);
    const passo = Math.min(100, (TICK_MS / duracao) * 100);

    const timer = setInterval(() => {
      if (pausadoRef.current) return;

      setProgresso((anterior) => Math.max(0, anterior - passo));
    }, TICK_MS);

    return () => clearInterval(timer);
  }, [aberto, duracao, id, mensagem]);

  // O fechamento é controlado pelo progresso, o que permite pausar no hover.
  useEffect(() => {
    if (aberto && progresso <= 0) onFechar?.();
  }, [aberto, onFechar, progresso]);

  const handleClose = useCallback(
    (evento, motivo) => {
      if (motivo === 'clickaway') return;
      onFechar?.(evento, motivo);
    },
    [onFechar]
  );

  const handleMouseEnter = useCallback(() => {
    if (pausarNoHover) pausadoRef.current = true;
  }, [pausarNoHover]);

  const handleMouseLeave = useCallback(() => {
    pausadoRef.current = false;
  }, []);

  if (!aberto || !mensagem) return null;

  const { cor: corTom, Icone } = TONS[severidade];
  const preenchido = variant === 'filled';
  const contornado = variant === 'outlined';

  // Superfícies, bordas e textos derivados do tom do alerta + mode do tema.
  const fundoPadrao = escuro ? COLORS.darkPaper : COLORS.white;
  const fundoPreenchido = alpha(corTom, escuro ? 0.24 : 0.16);
  const fundoBase = preenchido ? fundoPreenchido : fundoPadrao;
  const fundo = contornado ? 'transparent' : fundoBase;
  const borda = alpha(corTom, preenchido || contornado ? 0.55 : 0.34);
  const fundoIcone = alpha(corTom, preenchido || escuro ? 0.24 : 0.14);
  const corTitulo = escuro ? COLORS.white : COLORS.textDark;
  const corMensagem = escuro ? COLORS.gray400 : COLORS.brandGray;
  const corFechar = escuro ? COLORS.gray400 : COLORS.gray700;

  return (
    <Snackbar
      open
      autoHideDuration={null}
      onClose={handleClose}
      anchorOrigin={origem}
      sx={{ maxWidth: 560, px: 1.5 }}
    >
      <Box
        role={severidade === 'error' ? 'alert' : 'status'}
        aria-live={severidade === 'error' ? 'assertive' : 'polite'}
        aria-atomic="true"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          width: '100%',
          maxWidth: { xs: '100%', sm: 420 },
          pl: 1.75,
          pr: 0.75,
          pt: 1.5,
          pb: 2,
          borderRadius: RAIO,
          overflow: 'hidden',
          bgcolor: fundo,
          border: `1px solid ${borda}`,
          boxShadow: escuro
            ? '0 18px 40px rgba(0, 0, 0, 0.48)'
            : '0 18px 40px rgba(28, 27, 25, 0.18)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: '10px',
            bgcolor: fundoIcone,
            color: corTom,
          }}
        >
          <Icone sx={{ fontSize: 20 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {titulo ? (
            <Typography
              component="p"
              sx={{ fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.35, color: corTitulo }}
            >
              {titulo}
            </Typography>
          ) : null}

          <Typography
            component="p"
            sx={{
              mt: titulo ? 0.25 : 0,
              fontSize: '0.875rem',
              lineHeight: 1.45,
              fontWeight: titulo ? 400 : 500,
              color: titulo ? corMensagem : corTitulo,
              wordBreak: 'break-word',
            }}
          >
            {mensagem}
          </Typography>

          {acao?.label ? (
            <Button
              size="small"
              onClick={acao.onClick}
              sx={{
                mt: 0.75,
                ml: -0.75,
                minWidth: 0,
                px: 1,
                py: 0.25,
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '0.8125rem',
                color: corTom,
                '&:hover': { bgcolor: alpha(corTom, 0.12) },
              }}
            >
              {acao.label}
            </Button>
          ) : null}
        </Box>

        <IconButton
          size="small"
          aria-label="Fechar alerta"
          onClick={() => onFechar?.()}
          sx={{
            flexShrink: 0,
            mt: -0.25,
            borderRadius: '10px',
            color: corFechar,
            transition: 'background-color 120ms ease, color 120ms ease',
            '&:hover': { bgcolor: alpha(corTom, 0.12), color: corTom },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>

        {/* Progresso aninhado na base: recortado pelo raio do card. */}
        <LinearProgress
          variant="determinate"
          value={progresso}
          sx={{
            position: 'absolute',
            insetInline: 0,
            bottom: 0,
            height: 3,
            bgcolor: alpha(corTom, 0.16),
            '& .MuiLinearProgress-bar': {
              bgcolor: corTom,
              transition: 'none',
            },
          }}
        />
      </Box>
    </Snackbar>
  );
};

AlertaReutilizavel.propTypes = {
  id: PropTypes.string,
  mensagem: PropTypes.node.isRequired,
  tipo: PropTypes.oneOf(TIPOS),
  aberto: PropTypes.bool,
  onFechar: PropTypes.func,
  titulo: PropTypes.node,
  autoHideDuration: PropTypes.number,
  posicao: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  variant: PropTypes.oneOf(['standard', 'filled', 'outlined']),
  acao: PropTypes.shape({
    label: PropTypes.node,
    onClick: PropTypes.func,
  }),
  pausarNoHover: PropTypes.bool,
};

export default AlertaReutilizavel;
